import React, { useEffect, useRef, useState } from "react";
import "./style.css";
import { useHistory } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import PuffLoader from "react-spinners/ClipLoader";
import { ADDRESS_FORM, POST } from "../../constant";
import { connect } from "react-redux";
import Labels from "../../services/apiLabels";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { mapSavedAddressToForm } from "../../utils/vehicleDetails";
import CountrySelect from "../CountrySelect";
import {
  getDefaultCountry,
  isCountryEnabled,
  resolveCountryName,
} from "../../utils/countries";
import {
  createPlacesSessionToken,
  fetchPlacesAutocomplete,
  fetchPlacesDetails,
  placesSearchConfig,
} from "../../utils/placesAddress";

function SearchCity(props) {
  const {
    sumit_form,
    addressformSuccessAt,
    addressformFailedAt,
    addressFormErrors,
    evaluation_id_,
    car_update_address,
    savedAddress,
    embedded,
    onComplete,
  } = props;

  const { feedback } = useContext(FeedbackContext);
  const carupdate_page = feedback?.carupdate_page || {};
  const history = useHistory();
  const searchFieldId = useRef(`vehicle-address-search-${Date.now()}`).current;
  const searchTimerRef = useRef(null);
  const hydratedAddressRef = useRef(false);
  const sessionTokenRef = useRef(createPlacesSessionToken());
  const searchAbortRef = useRef(null);
  const detailsAbortRef = useRef(null);
  const lastQueryRef = useRef("");
  const suggestionCacheRef = useRef(new Map());

  const [inpValue, setInpValue] = useState("");
  const [citiesList, setCitiesList] = useState([]);
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [line3, setLine3] = useState("");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState(() => getDefaultCountry().name);
  const [postCode, setPostCode] = useState("");
  const [show, setShow] = useState(false);
  const [addressEntryMode, setAddressEntryMode] = useState(null);
  const [divShow, setDivShow] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const submittedRef = useRef(false);

  const ensureSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = createPlacesSessionToken();
    }
    return sessionTokenRef.current;
  };

  const rotateSessionToken = () => {
    sessionTokenRef.current = createPlacesSessionToken();
    suggestionCacheRef.current.clear();
    lastQueryRef.current = "";
  };

  const applySavedAddress = (source) => {
    const mapped = mapSavedAddressToForm(source);
    if (!mapped) {
      return false;
    }

    setLine1(mapped.line1);
    setLine2(mapped.line2);
    setLine3(mapped.line3);
    setAddress(mapped.address);
    const savedCountry = resolveCountryName(mapped.country);
    setCountry(
      isCountryEnabled(savedCountry)
        ? savedCountry
        : getDefaultCountry().name
    );
    setPostCode(mapped.postCode);
    setShow(true);
    setAddressEntryMode("manual");
    setFormError("");
    return true;
  };

  useEffect(() => {
    if (hydratedAddressRef.current || !savedAddress) {
      return;
    }

    if (applySavedAddress(savedAddress)) {
      hydratedAddressRef.current = true;
    }
  }, [savedAddress]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (searchAbortRef.current) searchAbortRef.current.abort();
      if (detailsAbortRef.current) detailsAbortRef.current.abort();
    };
  }, []);

  const citiesListHandler = (e) => {
    const value = e.target.value;
    setInpValue(value);
    setSearchError("");

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    const trimmed = value.trim();
    // Do not call search until length is greater than 3.
    if (trimmed.length <= 3) {
      if (searchAbortRef.current) searchAbortRef.current.abort();
      setCitiesList([]);
      setDivShow(false);
      setSearchLoading(false);
      return;
    }

    // Debounce: only request after the user pauses typing.
    searchTimerRef.current = setTimeout(() => {
      getAllCitiesList(trimmed);
    }, placesSearchConfig.debounceMs);
  };

  async function getAllCitiesList(query) {
    const normalized = String(query || "").trim();
    if (normalized.length <= 3) return;

    // Skip identical query we already resolved this session (use cache / last query).
    if (normalized === lastQueryRef.current) {
      const existing =
        suggestionCacheRef.current.get(normalized.toLowerCase()) || [];
      if (existing.length) {
        setCitiesList(existing);
        setDivShow(true);
        setSearchLoading(false);
        return;
      }
    }

    const cacheKey = normalized.toLowerCase();
    const cached = suggestionCacheRef.current.get(cacheKey);
    if (cached) {
      lastQueryRef.current = normalized;
      setCitiesList(cached);
      setDivShow(cached.length > 0);
      setSearchLoading(false);
      setSearchError(
        cached.length
          ? ""
          : "No addresses matched your search. Try a different term or enter your address manually."
      );
      return;
    }

    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setSearchLoading(true);
    setSearchError("");

    try {
      const options = await fetchPlacesAutocomplete({
        input: normalized,
        sessionToken: ensureSessionToken(),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      lastQueryRef.current = normalized;
      suggestionCacheRef.current.set(cacheKey, options);
      if (suggestionCacheRef.current.size > 30) {
        const firstKey = suggestionCacheRef.current.keys().next().value;
        suggestionCacheRef.current.delete(firstKey);
      }

      setCitiesList(options);
      if (options.length > 0) {
        setDivShow(true);
      } else {
        setDivShow(false);
        setSearchError(
          "No addresses matched your search. Try a different term or enter your address manually."
        );
      }
    } catch (err) {
      if (err?.name === "AbortError" || controller.signal.aborted) return;
      setCitiesList([]);
      setDivShow(false);
      setSearchError(
        "Address search is unavailable right now. Please enter your address manually below."
      );
    } finally {
      if (!controller.signal.aborted) setSearchLoading(false);
    }
  }

  const cityData = (item) => {
    const label = item?.label || "";
    const placeId = item?.placeId || item?.id || "";
    setInpValue(label);
    setDivShow(false);
    setCitiesList([]);
    if (!placeId) {
      setSearchError("Please choose an address from the suggestions.");
      return;
    }
    getOneCity(placeId, label);
  };

  const applyMappedPlace = (mapped, fallbackLabel = "") => {
    const resolvedLine1 =
      String(mapped.line1 || mapped.address || "").trim() ||
      String(fallbackLabel || "")
        .split(",")[0]
        .trim();
    const resolvedCity = String(mapped.line3 || "").trim();
    const resolvedAddress =
      String(mapped.address || mapped.line1 || "").trim() ||
      fallbackLabel ||
      resolvedLine1;
    const resolvedPostCode = String(mapped.postCode || "").trim().toUpperCase();
    const resolvedCountry = resolveCountryName(
      mapped.countryCode || mapped.country || ""
    );
    const countryValue = isCountryEnabled(resolvedCountry)
      ? resolvedCountry
      : getDefaultCountry().name;

    setLine1(resolvedLine1);
    setLine2(String(mapped.line2 || "").trim());
    setLine3(resolvedCity);
    setAddress(resolvedAddress);
    setCountry(countryValue);
    setPostCode(resolvedPostCode);
    setAddressEntryMode("lookup");
    setShow(true);
    setFormError("");
  };

  async function getOneCity(placeId, fallbackLabel = "") {
    setSearchError("");
    if (detailsAbortRef.current) detailsAbortRef.current.abort();
    const controller = new AbortController();
    detailsAbortRef.current = controller;
    setSearchLoading(true);

    try {
      const mapped = await fetchPlacesDetails({
        placeId,
        sessionToken: ensureSessionToken(),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      applyMappedPlace(mapped, fallbackLabel);
      // End Places session after details (billing / quota best practice).
      rotateSessionToken();
    } catch (err) {
      if (err?.name === "AbortError" || controller.signal.aborted) return;
      setSearchError(
        "We could not look up that address. Please enter it manually."
      );
    } finally {
      if (!controller.signal.aborted) setSearchLoading(false);
    }
  }

  const validateAddress = () => {
    if (!evaluation_id_) {
      return "Your session has expired. Please go back and open vehicle details again.";
    }
    if (!line1.trim() && !address.trim()) {
      return "Please enter line 1. You can also fill the address field and we will use it as line 1.";
    }
    if (!country.trim()) {
      return "Please enter your country.";
    }
    if (!line3.trim()) {
      return `Please enter your ${carupdate_page.City_text || "city"}.`;
    }
    if (!postCode.trim()) {
      return "Please enter your postcode.";
    }
    return "";
  };

  const formatAddressErrors = (errors = {}) => {
    const messages = Object.values(errors)
      .flat()
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(" ");
    }

    return "We could not save your address. Please check the details and try again.";
  };

  const submitAddress = () => {
    const validationError = validateAddress();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError("");
    setSubmitting(true);
    submittedRef.current = true;

    const resolvedLine1 = line1.trim() || address.trim();
    const resolvedAddress = address.trim() || line1.trim();
    const resolvedLine2 = line2.trim() || "-";
    const resolvedCity = line3.trim();

    sumit_form({
      evaluation_id: evaluation_id_,
      line1: resolvedLine1,
      line2: resolvedLine2,
      line3: resolvedCity,
      city: resolvedCity,
      address: resolvedAddress,
      country: country.trim(),
      post_code: postCode.trim(),
    });

    car_update_address({
      address: resolvedAddress,
      city: resolvedCity,
      country: country.trim(),
      id: 11,
      line1: resolvedLine1,
      line2: resolvedLine2,
      line3: resolvedCity,
      post_code: postCode.trim(),
    });
  };

  useEffect(() => {
    if (!submittedRef.current || !addressformSuccessAt) {
      return;
    }

    submittedRef.current = false;
    setSubmitting(false);
    setFormError("");

    if (onComplete) {
      onComplete();
      return;
    }

    history.push("/carsteps");
  }, [addressformSuccessAt, onComplete, history]);

  useEffect(() => {
    if (!submittedRef.current || !addressformFailedAt) {
      return;
    }

    submittedRef.current = false;
    setSubmitting(false);
    setFormError(formatAddressErrors(addressFormErrors));
  }, [addressformFailedAt, addressFormErrors]);

  const openManualForm = () => {
    setAddressEntryMode("manual");
    setFormError("");
    setSearchError("");
    setShow(true);
    if (!String(country || "").trim()) {
      setCountry(getDefaultCountry().name);
    }
  };

  const backToSearch = () => {
    setShow(false);
    setAddressEntryMode(null);
    setDivShow(false);
    setCitiesList([]);
    setFormError("");
    setSearchError("");
    rotateSessionToken();
  };

  return (
    <div className="pi-car-update__address">
      {show ? (
        <div className="pi-car-update__address-form">
          {!embedded ? (
            <>
              <p className="pi-car-update__eyebrow">Final step</p>
              <h2 className="pi-car-update__question-title">Postal address</h2>
              <p className="pi-car-update__question-lead">
                Confirm where the vehicle is located.
              </p>
            </>
          ) : null}

          <div className="pi-car-update__fields">
            <label className="pi-car-update__field">
              <span>{carupdate_page.Line_1_text} *</span>
              <input
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder={carupdate_page.Line_1_placeholder}
              />
            </label>

            <label className="pi-car-update__field">
              <span>
                {carupdate_page.Line_2_text}
                <span className="pi-car-update__field-hint"> (optional)</span>
              </span>
              <input
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                placeholder={carupdate_page.Line_2_placeholder}
              />
            </label>

            <label className="pi-car-update__field">
              <span>{carupdate_page.City_text || "City"} *</span>
              <input
                value={line3}
                onChange={(e) => setLine3(e.target.value)}
                placeholder={carupdate_page.City_placeholder || "City"}
                required
              />
            </label>

            <label className="pi-car-update__field">
              <span>{carupdate_page.Address_text}</span>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={carupdate_page.Address_text}
              />
            </label>

            <label className="pi-car-update__field" htmlFor="vehicle-address-country">
              <span>{carupdate_page.Country_text} *</span>
              <CountrySelect
                id="vehicle-address-country"
                name="country"
                value={country}
                required
                onChange={(nextCountry) => setCountry(nextCountry)}
                className="pi-car-update__select"
                disabled={submitting}
              />
            </label>

            <label className="pi-car-update__field">
              <span>{carupdate_page.Postcode_text} *</span>
              <input
                onChange={(e) => setPostCode(e.target.value.toUpperCase())}
                value={postCode}
                placeholder={carupdate_page.Postcode_placeholder}
              />
            </label>
          </div>

          {formError ? (
            <p className="pi-car-update__form-error" role="alert">
              {formError}
            </p>
          ) : null}

          {addressEntryMode === "lookup" ? (
            <button
              type="button"
              className="pi-car-update__link-btn"
              onClick={backToSearch}
              disabled={submitting}
            >
              Search for a different address
            </button>
          ) : null}

          <button
            type="button"
            className="pi-car-update__btn pi-car-update__btn--primary pi-car-update__btn--block"
            onClick={submitAddress}
            disabled={submitting}
          >
            {submitting ? "Saving…" : carupdate_page.Submit_text}
          </button>
        </div>
      ) : (
        <div className="pi-car-update__search">
          <button type="button" className="pi-car-update__link-btn" onClick={openManualForm}>
            {carupdate_page.If_you_cannot || "If you cannot find your address,"}
            <span> {carupdate_page.Click_to_enter || "click to enter manually"}</span>
          </button>

          <div className="pi-car-update__search-field" id={searchFieldId}>
            <input
              type="text"
              placeholder={
                carupdate_page.serch_placeholder ||
                "Search address (min. 4 characters)"
              }
              onChange={citiesListHandler}
              value={inpValue}
              autoComplete="street-address"
            />
            <span className="pi-car-update__search-icon" aria-hidden>
              {searchLoading ? <PuffLoader size={18} /> : <FaSearch size={16} />}
            </span>
          </div>

          {inpValue.trim().length > 0 && inpValue.trim().length <= 3 ? (
            <p className="pi-car-update__field-hint" style={{ marginTop: 8 }}>
              Keep typing — search starts after 4 characters.
            </p>
          ) : null}

          {searchError ? (
            <p className="pi-car-update__form-error" role="alert">
              {searchError}
            </p>
          ) : null}

          {divShow && citiesList.length > 0 ? (
            <ul className="pi-car-update__city-list">
              {citiesList.map((item) => (
                <li key={item.key || item.placeId}>
                  <button type="button" onClick={() => cityData(item)}>
                    <span>{item.mainText || item.label}</span>
                    {item.secondaryText ? (
                      <span className="pi-car-update__field-hint">
                        {" "}
                        · {item.secondaryText}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  addressformSuccessAt: state.cardeailupdate.addressformSuccessAt,
  addressformFailedAt: state.cardeailupdate.addressformFailedAt,
  addressFormErrors: state.cardeailupdate.addressFormErrors,
  evaluation_id_: state.sellMyCar.evaluation_create.id,
  savedAddress: state.sellMyCar.evaluation_create.vehicle_address?.[0],
});

const mapDispatchToProps = (dispatch) => ({
  sumit_form: (data) =>
    dispatch({
      type: ADDRESS_FORM,
      payload: {
        type: ADDRESS_FORM,
        method: POST,
        label: Labels.address_form,
        hideOverlayLoader: true,
        data,
      },
    }),
  car_update_address: (data_a) =>
    dispatch({
      type: "CAR_UPDATE_ADDRESS",
      payload: {
        type: "CAR_UPDATE_ADDRESS",
        data: data_a,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchCity);
